import React, { useEffect, useState, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { useParams } from 'react-router-dom';
import { formatPrice } from '../utils/formatPrice';
import { Customer, CustomerDiscount, Note, ReservationFieldDef } from '../interfaces';
import ReservationService from '../services/ReservationService';
import Select from 'react-select';
import SuccessNotification from '../components/notifications/SuccessNotification';
import CircularProgress from '@mui/material/CircularProgress';
import Spinner from '../components/Spinner';
import { format } from 'date-fns';
import EditNotesPopup from '../components/popups/EditNotesPopup';
import './EditCustomer.css';

const defaultCustomer: Customer = {
    id: '00000000-0000-0000-0000-000000000000',
    propertyId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    companyName: '',
    companyTaxIDNumber: '',
    streetAddress: '',
    aptSuiteFloor: '',
    city: '',
    state: '',
    postalCode: '',
    notes: [],
    history: [],
    totalOrderAmount: 0
};

const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' }
];

const EditCustomer: React.FC = () => {
    const [, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    const { customerId } = useParams<{ customerId: string }>();
    const [customer, setCustomer] = useState<Customer>(defaultCustomer);
    const emptyGuid = '00000000-0000-0000-0000-000000000000';
    const [selectedTab, setSelectedTab] = useState('details');
    const [showSuccess, setShowSuccess] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const [title, setTitle] = useState('');
    const [showSpinner, setShowSpinner] = useState(false);
    const [imageLoading, setimageLoading] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');
    const notesTabs = [
        { label: 'Active Notes', value: 'Active Notes' },
        { label: 'Archived Notes', value: 'Archived Notes' },
    ];
    const [selectedNotesTab, setSelectedNotesTab] = useState('Active Notes');
    const [noteOptionsOpen, setNoteOptionsOpen] = useState<string | null>(null);
    const noteOptionsRef = useRef(null);
    const [editNoteDrawerOpen, setEditNoteDrawerOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);
    const [discounts, setDiscounts] = useState<CustomerDiscount[]>([]);

    /** Reservation questions the venue chose to keep against the guest. */
    const [profileFields, setProfileFields] = useState<ReservationFieldDef[]>([]);
    useEffect(() => {
        let alive = true;
        ReservationService.getFieldDefinitions()
            .then(defs => {
                if (alive) setProfileFields(defs.filter(f => f.onCustomerProfile && f.isActive));
            })
            .catch(() => { /* the profile still works without them */ });
        return () => { alive = false; };
    }, []);

    const setProfileFieldValue = (key: string, value: unknown) => {
        setCustomer(prevCustomer => ({
            ...prevCustomer,
            reservationFields: { ...(prevCustomer.reservationFields || {}), [key]: value },
        }));
    };

    /**
     * Rendered with this page's own controls rather than the booking sheet's, so the section
     * reads as part of the profile instead of something bolted on from another screen.
     */
    const renderProfileField = (field: ReservationFieldDef) => {
        const value = customer.reservationFields?.[field.key];

        switch (field.type) {
            case 'checkbox':
                return (
                    <div className='d-flex align-items-center' style={{ gap: '8px' }}>
                        <input
                            type='checkbox'
                            id={`prof-${field.key}`}
                            checked={value === true}
                            onChange={e => setProfileFieldValue(field.key, e.target.checked)}
                        />
                        <label htmlFor={`prof-${field.key}`} style={{ margin: 0, fontSize: '13px' }}>
                            {field.helpText || 'Yes'}
                        </label>
                    </div>
                );
            case 'textarea':
                return (
                    <textarea
                        className='ec-input'
                        rows={2}
                        value={(value as string) || ''}
                        placeholder={field.helpText}
                        onChange={e => setProfileFieldValue(field.key, e.target.value)}
                    />
                );
            case 'select':
                return (
                    <select
                        className='ec-input'
                        value={(value as string) || ''}
                        onChange={e => setProfileFieldValue(field.key, e.target.value)}
                    >
                        <option value=''>{field.helpText || 'Not set'}</option>
                        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                );
            default:
                return (
                    <input
                        type={field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                        className='ec-input'
                        value={(value as string) || ''}
                        placeholder={field.helpText}
                        onChange={e => setProfileFieldValue(field.key, e.target.value)}
                    />
                );
        }
    };
    const [productCategories, setProductCategories] = useState<{ id: string; name: string }[]>([]);
    const [openDiscountDropdown, setOpenDiscountDropdown] = useState<string | null>(null);

  
    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };
    
        document.addEventListener("visibilitychange", handleVisibilityChange);
    
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
    
        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);
    
        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);
    
    useEffect(() => {
        const handleResize = () => {
          setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
          });
        };
    
        window.addEventListener("resize", handleResize);
    
        return () => window.removeEventListener("resize", handleResize);
      }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.discount-category-select')) {
                setOpenDiscountDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchProductCategories = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/ProductCategory`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    setProductCategories(data || []);
                }
            } catch (error) {
                console.error('Error fetching product categories: ', error);
            }
        };
        fetchProductCategories();
    }, []);

    useEffect(() => {
        const fetchCustomer = async () => {
            if (customerId && customerId !== emptyGuid) {
                setShowSpinner(true);
                await fetchCustomerById(customerId);
                setShowSpinner(false);
            } else {
                setCustomer(defaultCustomer);
                setTitle('Add Customer');
            }
        };

        fetchCustomer();
    }, [customerId]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setimageLoading(true);
        const file = event.target.files ? event.target.files[0] : null;

        if (file) {
            const filePath = file.name;
            const fileContentType = file.type;

            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onloadend = async () => {
                const base64File = typeof reader.result === 'string' ? reader.result.split(',')[1] : '';

                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer/upload-profile-img/`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            file: base64File,
                            contentType: fileContentType,
                            path: filePath
                        })
                    });
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const result = await response.text();
                    setCustomer(prevCustomer => ({
                        ...prevCustomer,
                        profileImageUrl: result
                    }));
                    setimageLoading(false);
                } catch (error) {
                    console.error("Error when uploading file: ", error);
                }
            };
        }
    };


    const fetchCustomerById = async (id: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer/${id}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setCustomer(data);
            setTitle(data.firstName + " " + data.lastName)
            setDiscounts(data.discounts || []);
        } catch (error) {
            console.error("Error fetching customer data: ", error);
        }
    };

    const saveCustomer = async () => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: customer.id,
                    propertyId: customer.propertyId || '00000000-0000-0000-0000-000000000000',
                    firstName: customer.firstName,
                    lastName: customer.lastName,
                    email: customer.email,
                    phone: customer.phone,
                    gender: customer.gender,
                    companyName: customer.companyName,
                    companyTaxIDNumber: customer.companyTaxIDNumber,
                    streetAddress: customer.streetAddress,
                    aptSuiteFloor: customer.aptSuiteFloor,
                    profileImageUrl: customer.profileImageUrl,
                    city: customer.city,
                    state: customer.state,
                    postalCode: customer.postalCode,
                    notes: customer.notes,
                    history: customer.history,
                    discounts: discounts
                }),
                credentials: 'include',
            });

            if (response) {
                if (customerId) {
                    await fetchCustomerById(customerId);
                }
                setShowSpinner(false);
                setHeaderTitle("Success!");
                setContent("Customer was saved successfully.")
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    };

    const saveUpdatedCustomer = async (updatedCustomer: Customer) => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: updatedCustomer.id,
                    propertyId: updatedCustomer.propertyId || '00000000-0000-0000-0000-000000000000',
                    firstName: updatedCustomer.firstName,
                    lastName: updatedCustomer.lastName,
                    email: updatedCustomer.email,
                    phone: updatedCustomer.phone,
                    gender: updatedCustomer.gender,
                    companyName: updatedCustomer.companyName,
                    companyTaxIDNumber: updatedCustomer.companyTaxIDNumber,
                    streetAddress: updatedCustomer.streetAddress,
                    aptSuiteFloor: updatedCustomer.aptSuiteFloor,
                    profileImageUrl: updatedCustomer.profileImageUrl,
                    city: updatedCustomer.city,
                    state: updatedCustomer.state,
                    postalCode: updatedCustomer.postalCode,
                    notes: updatedCustomer.notes,
                    history: updatedCustomer.history,
                    discounts: updatedCustomer.discounts || discounts
                }),
                credentials: 'include',
            });

            if (response) {
                if (customerId) {
                    await fetchCustomerById(customerId);
                }
                setShowSpinner(false);
                setHeaderTitle("Success!");
                setContent("Customer information was saved successfully.")
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCustomer(prevCustomer => ({
            ...prevCustomer,
            [name]: value
        }));
    };

    const handleGenderChange = (selectedOption: { value: string, label: string } | null) => {
        setCustomer(prevCustomer => ({
            ...prevCustomer,
            gender: selectedOption ? selectedOption.value : ''
        }));
    };

    const formatDate = (date: Date | undefined) => {
        if (!date) return "-";

        const dateObj = new Date(date);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = dateObj.toLocaleString('default', { month: 'short' });
        const year = dateObj.getFullYear();

        return `${day} ${month} ${year}`;
    };

    const calculateAverageBill = () => {
        if (!customer.history || customer.history.length === 0) return 0;

        const totalAmount = customer.history.reduce((sum, history) => sum + history.orderAmount, 0);
        const average = totalAmount / customer.history.length;

        return average;
    };

    const calculateTotalRevenue = () => {
        if (!customer.history || customer.history.length === 0) return 0;
        const totalAmount = customer.history.reduce((sum, history) => sum + history.orderAmount, 0);
        return totalAmount;
    };

    const generateUUID = () => {
        let d = new Date().getTime();
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            d += performance.now();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    };

    const handleSaveNote = async () => {
        const email = localStorage.getItem('userName');

        if (!customer?.notes) {
            customer.notes = [];
        }

        customer.notes?.push({
            id: generateUUID(),
            isArchived: false,
            date: new Date(),
            note: newNoteText,
            addedBy: email || '',
        });

        setNewNoteText('');
        await saveCustomer();
    };

    const formatNoteDate = (date: Date) => {
        return format(date, 'dd/MM/yyyy');
    };

    const formatTime = (date: Date) => {
        return format(date, 'hh:mm');
    };

    const handleToggleOptions = (noteId: string) => {
        setNoteOptionsOpen(noteOptionsOpen === noteId ? null : noteId);
    };

    const handleArchiveNote = async (noteId: string) => {
        const updatedGuestNotes = customer.notes?.map(note =>
            note.id === noteId
                ? { ...note, isArchived: true }
                : note
        );

        const updatedCustomer = {
            ...customer,
            notes: updatedGuestNotes
        };
        await saveUpdatedCustomer(updatedCustomer);
        setNoteOptionsOpen(null);
    };

    const handleEditNote = (note: Note) => {
        setEditNoteDrawerOpen(true);
        setSelectedNote(note);
        setNoteOptionsOpen(null);
    };

    const handleSaveEditNote = async (updatedNoteText: string) => {
        if (!selectedNote) return;

        const noteExistsInGuestNotes = customer.notes?.some(note => note.id === selectedNote.id);

        if (noteExistsInGuestNotes) {
            const updatedNotes = customer.notes.map(note =>
                note.id === selectedNote.id ? { ...note, note: updatedNoteText } : note
            );
            const updatedCustomer = {
                ...customer,
                notes: updatedNotes
            };
            await saveUpdatedCustomer(updatedCustomer);

        }

        setSelectedNote(null);
    };


    return (
        <div className='edit-customer-view ps-scope'>
            <header className='ec-head'>
                <div className='ec-title'>{title || 'Customer'}</div>
            </header>
            <div className='ec-tabs'>
                <button className={selectedTab == 'details' ? "ec-tab is-on" : "ec-tab"} onClick={() => setSelectedTab('details')}>
                    Details
                </button>
                <button className={selectedTab == 'history' ? "ec-tab is-on" : "ec-tab"} onClick={() => setSelectedTab('history')}>
                    History
                </button>
                <button className={selectedTab == 'notes' ? "ec-tab is-on" : "ec-tab"} onClick={() => setSelectedTab('notes')}>
                    Notes ({customer?.notes?.length})
                </button>
                <button className={selectedTab == 'documents' ? "ec-tab is-on" : "ec-tab"} onClick={() => setSelectedTab('documents')}>
                    Documents
                </button>
            </div>
            {
                selectedTab == 'details' &&
                <div className='two-col-layout mt-4 gap-3 flex-column-reverse flex-lg-row'>
                    <div className='ec-card ec-form w-100'>
                        <h6 className='ec-eyebrow'>CONTACT INFORMATION</h6>
                        <div className='ec-fieldgrid'>
                            <div className='ec-field'>
                                <label className='ec-label'>First Name</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="firstName"
                                    value={customer.firstName || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>Last Name</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="lastName"
                                    value={customer.lastName || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field ec-field--full'>
                                <label className='ec-label'>Email Address</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="email"
                                    value={customer.email || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>Phone</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="phone"
                                    value={customer.phone || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field ec-select'>
                                <label className='ec-label'>Gender</label>
                                <Select
                                    classNamePrefix="ecsel"
                                    value={genderOptions.find(option => option.value === customer.gender)}
                                    onChange={handleGenderChange}
                                    options={genderOptions}
                                    placeholder="Select Gender"
                                    isClearable
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>Company Name</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="companyName"
                                    value={customer.companyName || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>Company Tax ID Number</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="companyTaxIDNumber"
                                    value={customer.companyTaxIDNumber || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <h6 className='ec-eyebrow' style={{ marginTop: 'var(--space-5)' }}>DETAILS ADDRESS</h6>
                        <div className='ec-fieldgrid'>
                            <div className='ec-field ec-field--full'>
                                <label className='ec-label'>Street Address</label>
                                <input
                                    type='text'
                                    className='ec-input'
                                    name="streetAddress"
                                    value={customer.streetAddress || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field ec-field--full'>
                                <label className='ec-label'>Apt, suite, floor etc.</label>
                                <input
                                    className='ec-input'
                                    name="aptSuiteFloor"
                                    value={customer.aptSuiteFloor || ''}
                                    onChange={handleInputChange}
                                    type='text'
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>City</label>
                                <input
                                    className='ec-input'
                                    name="city"
                                    type='text'
                                    value={customer.city || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>Province / State</label>
                                <input
                                    className='ec-input'
                                    name="state"
                                    type='text'
                                    value={customer.state || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='ec-field'>
                                <label className='ec-label'>Postal / ZIP Code</label>
                                <input
                                    className='ec-input'
                                    name="postalCode"
                                    type='text'
                                    value={customer.postalCode || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className='d-flex mt-4 justify-content-end'>
                            <button className='ec-btn ec-btn--primary' onClick={saveCustomer}>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.83333 1.5V4.33333C4.83333 4.80004 4.83333 5.0334 4.92416 5.21166C5.00406 5.36846 5.13154 5.49594 5.28834 5.57584C5.4666 5.66667 5.69996 5.66667 6.16667 5.66667H11.8333C12.3 5.66667 12.5334 5.66667 12.7117 5.57584C12.8685 5.49594 12.9959 5.36846 13.0758 5.21166C13.1667 5.0334 13.1667 4.80004 13.1667 4.33333V2.33333M13.1667 16.5V11.1667C13.1667 10.7 13.1667 10.4666 13.0758 10.2883C12.9959 10.1315 12.8685 10.0041 12.7117 9.92416C12.5334 9.83333 12.3 9.83333 11.8333 9.83333H6.16667C5.69996 9.83333 5.4666 9.83333 5.28834 9.92416C5.13154 10.0041 5.00406 10.1315 4.92416 10.2883C4.83333 10.4666 4.83333 10.7 4.83333 11.1667V16.5M16.5 6.77124V12.5C16.5 13.9001 16.5 14.6002 16.2275 15.135C15.9878 15.6054 15.6054 15.9878 15.135 16.2275C14.6002 16.5 13.9001 16.5 12.5 16.5H5.5C4.09987 16.5 3.3998 16.5 2.86502 16.2275C2.39462 15.9878 2.01217 15.6054 1.77248 15.135C1.5 14.6002 1.5 13.9001 1.5 12.5V5.5C1.5 4.09987 1.5 3.3998 1.77248 2.86502C2.01217 2.39462 2.39462 2.01217 2.86502 1.77248C3.3998 1.5 4.09987 1.5 5.5 1.5H11.2288C11.6364 1.5 11.8402 1.5 12.0321 1.54605C12.2021 1.58688 12.3647 1.65422 12.5138 1.7456C12.682 1.84867 12.8261 1.9928 13.1144 2.28105L15.719 4.88562C16.0072 5.17387 16.1513 5.318 16.2544 5.48619C16.3458 5.63531 16.4131 5.79789 16.4539 5.96795C16.5 6.15976 16.5 6.36358 16.5 6.77124Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                Save Changes
                            </button>
                        </div>
                    </div>
                    <div className='d-flex flex-column w-100 gap-3'>
                        <div className='ec-card ec-upload d-flex gap-3'>
                            {
                                imageLoading ?
                                    <div style={{ width: '80px' }} className='d-flex justify-content-center align-items-center'>
                                        <CircularProgress
                                            style={{
                                                width: 30,
                                                height: 30,
                                                zIndex: 3,
                                            }}
                                        />
                                    </div>
                                    :
                                    <>
                                        {
                                            customer.profileImageUrl && customer.profileImageUrl != '' ?
                                                <img src={customer.profileImageUrl} className='ec-avatar' />
                                                :
                                                <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="50" cy="50" r="50" fill="#D9D9D9" />
                                                    <path d="M63.3332 65C63.3332 62.6741 63.3332 61.5111 63.0461 60.5648C62.3998 58.4341 60.7324 56.7667 58.6017 56.1204C57.6554 55.8333 56.4924 55.8333 54.1665 55.8333H45.8332C43.5072 55.8333 42.3443 55.8333 41.3979 56.1204C39.2673 56.7667 37.5999 58.4341 36.9536 60.5648C36.6665 61.5111 36.6665 62.6741 36.6665 65M57.4998 42.5C57.4998 46.6421 54.142 50 49.9998 50C45.8577 50 42.4998 46.6421 42.4998 42.5C42.4998 38.3579 45.8577 35 49.9998 35C54.142 35 57.4998 38.3579 57.4998 42.5Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                        }
                                    </>
                            }

                            <div className='d-flex flex-column'>
                                <label className='ec-label'>
                                    Your photo
                                </label>
                                <small>This will be displayed on your profile.</small>
                                <div className='d-flex mt-1 align-items-center'>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.41009 0.910826C8.73553 0.585389 9.26317 0.585389 9.5886 0.910826L13.7553 5.07749C14.0807 5.40293 14.0807 5.93057 13.7553 6.256C13.4298 6.58144 12.9022 6.58144 12.5768 6.256L9.83268 3.51193L9.83268 11.5001C9.83268 11.9603 9.45959 12.3334 8.99935 12.3334C8.53911 12.3334 8.16602 11.9603 8.16602 11.5001L8.16602 3.51193L5.42194 6.256C5.0965 6.58144 4.56886 6.58144 4.24343 6.256C3.91799 5.93057 3.91799 5.40293 4.24343 5.07749L8.41009 0.910826Z" fill="#1643E0" />
                                        <path d="M1.49935 10.6667C1.95959 10.6667 2.33268 11.0398 2.33268 11.5001V12.5001C2.33268 13.2139 2.33333 13.6991 2.36398 14.0742C2.39383 14.4396 2.44793 14.6264 2.51434 14.7567C2.67413 15.0703 2.9291 15.3253 3.2427 15.4851C3.37303 15.5515 3.55987 15.6056 3.92522 15.6355C4.30029 15.6661 4.78553 15.6667 5.49935 15.6667H12.4994C13.2132 15.6667 13.6984 15.6661 14.0735 15.6355C14.4388 15.6056 14.6257 15.5515 14.756 15.4851C15.0696 15.3253 15.3246 15.0703 15.4844 14.7567C15.5508 14.6264 15.6049 14.4396 15.6347 14.0742C15.6654 13.6991 15.666 13.2139 15.666 12.5001V11.5001C15.666 11.0398 16.0391 10.6667 16.4993 10.6667C16.9596 10.6667 17.3327 11.0398 17.3327 11.5001V12.5345C17.3327 13.2053 17.3327 13.7589 17.2959 14.2099C17.2576 14.6784 17.1754 15.1089 16.9694 15.5134C16.6498 16.1406 16.1399 16.6505 15.5127 16.9701C15.1082 17.1762 14.6776 17.2583 14.2092 17.2966C13.7582 17.3334 13.2046 17.3334 12.5338 17.3334H5.46493C4.79413 17.3334 4.24049 17.3334 3.7895 17.2966C3.32108 17.2583 2.89049 17.1762 2.48605 16.9701C1.85884 16.6505 1.34891 16.1406 1.02933 15.5134C0.823253 15.1089 0.741116 14.6784 0.702845 14.2099C0.665997 13.7589 0.666006 13.2053 0.666016 12.5345V11.5001C0.666016 11.0398 1.03911 10.6667 1.49935 10.6667Z" fill="#1643E0" />
                                    </svg>
                                    <input onChange={handleFileChange} style={{ marginLeft: '-20px', fontSize: '12px' }} type="file" id="avatar" name="avatar" accept="image/png, image/jpeg, image/jpg, image/svg" />
                                </div>
                            </div>
                        </div>
                        <div className='ec-card d-flex flex-column'>
                            <h6 className='ec-eyebrow' style={{ marginBottom: 'var(--space-2)' }}>STATISTICS INFORMATION</h6>
                            <div className='ec-kv'>
                                <span className='ec-kv__k'>TOTAL RESERVATIONS</span>
                                <span className='ec-kv__v'>{customer.history ? customer.history.length : 0}</span>
                            </div>
                            <div className='ec-kv'>
                                <span className='ec-kv__k'>TOTAL REVENUE</span>
                                <span className='ec-kv__v'>{formatPrice(calculateTotalRevenue())}</span>
                            </div>
                            <div className='ec-kv'>
                                <span className='ec-kv__k'>AVERAGE BILL</span>
                                <span className='ec-kv__v'>{formatPrice(calculateAverageBill())}</span>
                            </div>
                            <div className='ec-kv'>
                                <span className='ec-kv__k'>LAST VISIT</span>
                                <span className='ec-kv__v'>{formatDate(customer.lastReservationDate)}</span>
                            </div>
                        </div>
                        {profileFields.length > 0 && (
                            <div className='ec-card d-flex flex-column'>
                                <h6 className='ec-eyebrow'>RESERVATION PREFERENCES</h6>
                                <small style={{ color: '#98A2B3' }}>
                                    Kept from their bookings and filled in again next time they reserve.
                                </small>
                                {profileFields.map(f => (
                                    <div key={f.id} className='ec-field'>
                                        <label className='ec-label'>{f.label}</label>
                                        {renderProfileField(f)}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className='ec-card d-flex flex-column'>
                            <div className='ec-disc-head'>
                                <h6 className='ec-eyebrow'>CUSTOMER DISCOUNTS</h6>
                                <button
                                    className='ec-btn ec-btn--primary ec-btn--sm'
                                    onClick={() => {
                                        setDiscounts([...discounts, {
                                            id: (typeof crypto !== 'undefined' && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : Date.now().toString(),
                                            categoryIds: [],
                                            categoryNames: [],
                                            applyToAll: false,
                                            type: 'percentage',
                                            value: 0
                                        }]);
                                    }}
                                >
                                    + Add
                                </button>
                            </div>
                            {discounts.length === 0 ? (
                                <div className='ec-disc-empty'>
                                    No discount rules configured yet.
                                </div>
                            ) : (
                                <div className='ec-disc-list mt-2'>
                                    {discounts.map((discount, index) => (
                                        <div key={discount.id} className='ec-disc-row'>
                                            <div className='ec-disc-inner'>
                                                <div className='discount-category-select ec-disc-cat'>
                                                    <div
                                                        className='ec-disc-trigger'
                                                        onClick={() => setOpenDiscountDropdown(openDiscountDropdown === discount.id ? null : discount.id)}
                                                    >
                                                        {discount.applyToAll ? (
                                                            <span className='ec-disc-all'>All Categories</span>
                                                        ) : discount.categoryNames.length > 0 ? (
                                                            <div className='ec-disc-tags'>
                                                                {discount.categoryNames.slice(0, 2).map((name, i) => (
                                                                    <span key={i} className='ec-disc-tag'>{name}</span>
                                                                ))}
                                                                {discount.categoryNames.length > 2 && (
                                                                    <span className='ec-disc-more'>+{discount.categoryNames.length - 2}</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className='ec-disc-placeholder'>Select categories...</span>
                                                        )}
                                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" style={{ flexShrink: 0 }}>
                                                            <path d="M1 1.5L6 6.5L11 1.5" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    {openDiscountDropdown === discount.id && (
                                                        <div className='ec-disc-dropdown'>
                                                            <div
                                                                className='ec-disc-option is-all'
                                                                onClick={() => {
                                                                    const updated = [...discounts];
                                                                    updated[index] = { ...updated[index], applyToAll: !updated[index].applyToAll };
                                                                    if (updated[index].applyToAll) {
                                                                        updated[index].categoryIds = [];
                                                                        updated[index].categoryNames = [];
                                                                    }
                                                                    setDiscounts(updated);
                                                                }}
                                                            >
                                                                <div className={`ec-disc-check ${discount.applyToAll ? 'is-on' : ''}`}>
                                                                    {discount.applyToAll && <span>✓</span>}
                                                                </div>
                                                                Apply to All
                                                            </div>
                                                            {productCategories.map(cat => {
                                                                const isSelected = discount.applyToAll || discount.categoryIds.includes(cat.id);
                                                                return (
                                                                    <div
                                                                        key={cat.id}
                                                                        className={`ec-disc-option ${discount.applyToAll ? 'is-disabled' : ''}`}
                                                                        onClick={() => {
                                                                            if (discount.applyToAll) return;
                                                                            const updated = [...discounts];
                                                                            if (updated[index].categoryIds.includes(cat.id)) {
                                                                                updated[index] = {
                                                                                    ...updated[index],
                                                                                    categoryIds: updated[index].categoryIds.filter(id => id !== cat.id),
                                                                                    categoryNames: updated[index].categoryNames.filter(n => n !== cat.name)
                                                                                };
                                                                            } else {
                                                                                updated[index] = {
                                                                                    ...updated[index],
                                                                                    categoryIds: [...updated[index].categoryIds, cat.id],
                                                                                    categoryNames: [...updated[index].categoryNames, cat.name]
                                                                                };
                                                                            }
                                                                            setDiscounts(updated);
                                                                        }}
                                                                    >
                                                                        <div className={`ec-disc-check ${isSelected ? 'is-on' : ''}`}>
                                                                            {isSelected && <span>✓</span>}
                                                                        </div>
                                                                        {cat.name}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className='ec-disc-valgroup'>
                                                    <button
                                                        className={`ec-disc-typebtn ${discount.type === 'percentage' ? 'is-on' : ''}`}
                                                        onClick={() => {
                                                            const updated = [...discounts];
                                                            updated[index] = { ...updated[index], type: 'percentage' };
                                                            setDiscounts(updated);
                                                        }}
                                                    >
                                                        %
                                                    </button>
                                                    <button
                                                        className={`ec-disc-typebtn ${discount.type === 'fixed' ? 'is-on' : ''}`}
                                                        onClick={() => {
                                                            const updated = [...discounts];
                                                            updated[index] = { ...updated[index], type: 'fixed' };
                                                            setDiscounts(updated);
                                                        }}
                                                    >
                                                        $
                                                    </button>
                                                    <input
                                                        type='number'
                                                        className='ec-disc-valinput'
                                                        placeholder='0'
                                                        min='0'
                                                        max={discount.type === 'percentage' ? 100 : undefined}
                                                        value={discount.value || ''}
                                                        onChange={(e) => {
                                                            const updated = [...discounts];
                                                            updated[index] = { ...updated[index], value: parseFloat(e.target.value) || 0 };
                                                            setDiscounts(updated);
                                                        }}
                                                    />
                                                </div>
                                                <button
                                                    className='ec-disc-remove'
                                                    onClick={() => setDiscounts(discounts.filter((_, i) => i !== index))}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            }

            {
                selectedTab == 'history' &&
                <div className='mt-4 ec-tablescroll'>
                    <div className='ec-htable'>
                        <div className="ec-hc ec-hc--head">
                            Order number
                        </div>
                        <div className="ec-hc ec-hc--head">
                            Order amount
                        </div>
                        <div className="ec-hc ec-hc--head">
                            Status
                        </div>
                        <div className="ec-hc ec-hc--head">
                            Waiter
                        </div>
                        {
                            customer?.history.map((hist, index) =>
                                <React.Fragment key={index}>
                                    <div className='ec-hc ec-hc--mono'>
                                        {hist.orderNumber}
                                    </div>
                                    <div className='ec-hc ec-hc--mono'>
                                        {hist.orderAmount}
                                    </div>
                                    <div className='ec-hc'>
                                        {hist.status}
                                    </div>
                                    <div className='ec-hc'>
                                        {hist.waiter}
                                    </div>
                                </React.Fragment>
                            )
                        }
                    </div>
                </div>
            }

            {
                selectedTab == 'notes' &&
                <div className='mt-4'>
                    <div className='d-flex flex-wrap mt-4 gap-3'>
                        <div className='col-12 col-lg-3 ec-notes-add'>
                            <h6 className='ec-section-title' style={{ marginTop: 0 }}>Add New Notes</h6>
                            <textarea
                                placeholder='Enter a description...'
                                rows={6}
                                className='ec-textarea mt-2'
                                value={newNoteText}
                                onChange={(e) => setNewNoteText(e.target.value)}
                            />
                            <div className='d-flex justify-content-end mt-3'>
                                <button className='ec-btn ec-btn--ghost' onClick={handleSaveNote}>Save Note</button>
                            </div>
                        </div>
                        <div className='col'>
                            <div className='ec-notes-tabs'>
                                {notesTabs.map((tab) => (
                                    <button
                                        key={tab.value}
                                        className={selectedNotesTab === tab.value ? 'ec-notes-tab is-on' : 'ec-notes-tab'}
                                        onClick={() => setSelectedNotesTab(tab.value)}>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className='ec-notes-scroll'>
                            <div className='ec-notes-grid'>
                                <div className="ec-nc ec-nc--head">
                                    Date
                                </div>
                                <div className="ec-nc ec-nc--head">
                                    Time
                                </div>
                                <div className="ec-nc ec-nc--head">
                                    Notes
                                </div>
                                <div className="ec-nc ec-nc--head">

                                </div>
                                {
                                    selectedNotesTab == 'Active Notes' &&
                                    <>
                                        <>
                                            {customer?.notes?.filter((note) => !note.isArchived).map((note) => (
                                                <>
                                                    <React.Fragment>
                                                        <div className='ec-nc'>
                                                            {formatNoteDate(note.date)}
                                                        </div>
                                                        <div className='ec-nc'>
                                                            {formatTime(note.date)}
                                                        </div>
                                                        <div className='ec-nc'>
                                                            {note.note}
                                                        </div>
                                                        <div className='ec-nc' style={{ position: 'relative', justifyContent: 'center' }}>
                                                            <button
                                                                className='ec-iconbtn'
                                                                onClick={() => handleToggleOptions(note.id)}
                                                            >
                                                                <svg width="4" height="16" viewBox="0 0 4 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M1.99935 8.83334C2.45959 8.83334 2.83268 8.46025 2.83268 8.00001C2.83268 7.53977 2.45959 7.16668 1.99935 7.16668C1.53911 7.16668 1.16602 7.53977 1.16602 8.00001C1.16602 8.46025 1.53911 8.83334 1.99935 8.83334Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                                    <path d="M1.99935 3.00001C2.45959 3.00001 2.83268 2.62691 2.83268 2.16668C2.83268 1.70644 2.45959 1.33334 1.99935 1.33334C1.53911 1.33334 1.16602 1.70644 1.16602 2.16668C1.16602 2.62691 1.53911 3.00001 1.99935 3.00001Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                                    <path d="M1.99935 14.6667C2.45959 14.6667 2.83268 14.2936 2.83268 13.8333C2.83268 13.3731 2.45959 13 1.99935 13C1.53911 13 1.16602 13.3731 1.16602 13.8333C1.16602 14.2936 1.53911 14.6667 1.99935 14.6667Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                                </svg>
                                                            </button>
                                                            {noteOptionsOpen === note.id && (
                                                                <div className='ec-menu' ref={noteOptionsRef}>
                                                                    <button onClick={() => handleArchiveNote(note.id)} className='ec-menuitem'>Archive</button>
                                                                    <button onClick={() => handleEditNote(note)} className='ec-menuitem'>Edit</button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </React.Fragment>
                                                </>
                                            ))}
                                        </>
                                    </>

                                }

                                {
                                    selectedNotesTab == 'Archived Notes' &&
                                    <>

                                        <>
                                            {customer?.notes?.filter((note) => note.isArchived).map((note) => (
                                                <>
                                                    <React.Fragment>
                                                        <div className='ec-nc'>
                                                            {formatNoteDate(note.date)}
                                                        </div>
                                                        <div className='ec-nc'>
                                                            {formatTime(note.date)}
                                                        </div>
                                                        <div className='ec-nc'>
                                                            {note.note}
                                                        </div>
                                                        <div className='ec-nc'>

                                                        </div>
                                                    </React.Fragment>
                                                </>
                                            ))}
                                        </>
                                    </>
                                }
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            }

            {
                selectedTab == 'documents' &&
                <div className='mt-4'>
                    <div className='ec-card d-flex flex-column'>
                        <h6 className='ec-eyebrow'>DOCUMENTS</h6>
                        <div className='ec-empty'>No documents available for this customer yet.</div>
                    </div>
                </div>
            }

            <div>
                {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            </div>
            {showSpinner && (
                <Spinner />
            )}
            {
                editNoteDrawerOpen &&
                <>
                    <div className="ec-modal-overlay"></div>
                    <EditNotesPopup
                        onClose={() => setEditNoteDrawerOpen(false)}
                        note={selectedNote}
                        onSave={handleSaveEditNote}
                    />
                </>

            }
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = StyleSheet.create({
    tabs: {
        display: 'flex',
        gap: 7,
        width: '60%'
    },
    input: {
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#D0D5DD',
        height: 35
    },
    textarea: {
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#D0D5DD',
    },
    label: {
        marginTop: 8,
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'Inter'
    },
    thumbsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 16,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#EAECF0',
        height: '100%',
        margin: 0,
        padding: 10
    }
});

export default EditCustomer;
